// pages/SAPProcessMap.jsx — "Process Map" view inside SAP Configuration (SPRO).
// Swimlane diagram (lane = SAP module, column = step order) of the real business
// processes in P01 ID / JB / KL. Data is a static snapshot in sapProcessMapData.js;
// Indonesian text in sapProcessMapData.id.js (ID/EN toggle, default follows the
// dashboard language). Light Tailwind classes only — dark.css remaps them.
// Selecting a company chip also filters the cycle tabs, step cards and step detail
// to that company's figures (sapProcessMapCo.js, generated per company code).
import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useLang } from '../context/LanguageContext';
import { MODULES } from './sapProcessMapData';
import { useProcMapLive, mergeLive } from './sapProcessMapLive';
import SAPProcessMapFindings from './SAPProcessMapFindings';
import { TEXT_ID, UI_ID, UI_EN } from './sapProcessMapData.id';
import { STATUS } from './sapProcessMapStd';
import { COMPANIES } from './sapCompanyData';
import SAPCompanyPanel, { COStructureCard } from './SAPCompanyPanel';
import SAPMasterDataCard from './SAPMasterDataCard';
import SAPProcessMapIssues from './SAPProcessMapIssues';
import SAPProcessMapOverview from './SAPProcessMapOverview';
import SAPDocInspector from './SAPDocInspector';
import SAPProcessMapTickets from './SAPProcessMapTickets';

// Comparison with the standard SAP ECC 6.0 flow for the selected cycle.
function StandardCompare({ std, lang, open, onToggle }) {
  if (!std) return null;
  const L = lang === 'id' ? 'id' : 'en';
  const used = [...new Set([...std.steps.map(s => s.status), ...std.diffs.map(d => d.status)])];
  const order = ['gap', 'extra', 'variant', 'skipped', 'unknown', 'same'];
  const diffs = [...std.diffs].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 flex flex-col gap-3">
      <button type="button" onClick={onToggle} className="flex flex-wrap items-center gap-2 text-left w-full">
        <span className="text-[14px] font-bold text-slate-800 mr-1">{L === 'id' ? 'Dibandingkan dengan standar SAP' : 'Compared with SAP standard'}</span>
        {order.filter(k => std.diffs.some(d => d.status === k)).map(k => {
          const n = std.diffs.filter(d => d.status === k).length;
          return (
            <span key={k} className="text-[10.5px] font-bold px-1.5 py-0.5 rounded border" style={{ color: STATUS[k].color, borderColor: STATUS[k].color }}>
              {n} {STATUS[k][L]}
            </span>
          );
        })}
        <span className="ml-auto text-[12px] font-semibold text-brand-700">{open ? (L === 'id' ? '▲ Tutup' : '▲ Hide') : (L === 'id' ? '▼ Lihat' : '▼ Show')}</span>
      </button>
      {open && (<>
      <div>
        <div className="text-[12.5px] text-slate-500 mt-0.5">
          {L === 'id'
            ? 'Referensi: alur standar SAP ECC 6.0 untuk siklus ini. Baris atas adalah langkah standar beserta statusnya di sistem kita.'
            : 'Reference: the standard SAP ECC 6.0 flow for this cycle. The top row lists the standard steps and their status in our system.'}
        </div>
      </div>
      <div className="flex flex-wrap items-stretch gap-1.5">
        {std.steps.map((s, i) => {
          const st = STATUS[s.status];
          return (
            <div key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-slate-300 text-sm" aria-hidden="true">→</span>}
              <div className="rounded-md border px-2.5 py-1.5 min-w-[130px] max-w-[190px] bg-white"
                style={{ borderColor: st.color, borderStyle: s.status === 'skipped' || s.status === 'unknown' ? 'dashed' : 'solid' }}
                title={st[L]}>
                <div className={`text-[12px] font-semibold leading-tight ${s.status === 'skipped' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{s[L]}</div>
                <div className="font-mono text-[10.5px] text-slate-400 mt-0.5">{s.tc}</div>
                <div className="text-[10.5px] font-bold mt-0.5" style={{ color: st.color }}>{st[L]}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-2">
        {diffs.map((d, i) => {
          const st = STATUS[d.status];
          return (
            <div key={i} className="flex gap-2.5 items-start text-[12.5px] text-slate-600">
              <span className="shrink-0 mt-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded border min-w-[118px] text-center"
                style={{ color: st.color, borderColor: st.color }}>{st[L]}</span>
              <span className="min-w-0">{d[L]}</span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] text-slate-400 border-t border-slate-100 pt-2">
        {order.filter(k => used.includes(k)).map(k => (
          <span key={k} className="flex items-center gap-1.5">
            <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: STATUS[k].color }} />{STATUS[k][L]}
          </span>
        ))}
      </div>
      </>)}
    </div>
  );
}

const LANG_KEY = 'sap_pmap_lang';
const numFmt = (n, lang) => n.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US');

function readLang(dashLang) {
  try { const v = localStorage.getItem(LANG_KEY); if (v === 'id' || v === 'en') return v; } catch { /* ignore */ }
  return dashLang === 'id' ? 'id' : 'en';
}

export default function SAPProcessMap() {
  // Figures and notes come from the database (procmap_*) when available, else from the static files.
  const live = useProcMapLive();
  const M = useMemo(() => mergeLive(live), [live]);
  const { LANDSCAPES, CO_FIGS, STD, TX } = M;
  const { lang: dashLang } = useLang();
  const [lang, setLang] = useState(() => readLang(dashLang));
  const [landKey, setLandKey] = useState('id');
  const [flowKey, setFlowKey] = useState('p2p');
  const [selId, setSelId] = useState(null);
  const [coCode, setCoCode] = useState(null); // selected company code (filters the diagram, drives the profile tab)
  // Deep link ?section=doc (dipakai AI Propose Solution di modal tiket SolMan → Document check).
  const [section, setSection] = useState(() => { try { const s = new URLSearchParams(window.location.search).get('section'); return ['overview', 'flow', 'company', 'org', 'notes', 'tickets', 'doc', 'about'].includes(s) ? s : 'overview'; } catch (_) { return 'overview'; } }); // overview | flow | company | org | notes | about
  const [ticketFilter, setTicketFilter] = useState(null); // filter handed to the Solman tickets section
  const [stdOpen, setStdOpen] = useState(false);
  const lanesRef = useRef(null);
  const [paths, setPaths] = useState([]);
  // Narrow screens: switch the swimlane to a vertical layout (lanes = columns, steps go down).
  const outerRef = useRef(null);
  const [perRow, setPerRow] = useState(99);

  const U = lang === 'id' ? UI_ID : UI_EN;
  const tr = useCallback(s => (lang === 'id' && s && (TX[s] || TEXT_ID[s])) || s, [lang, TX]);

  const land = LANDSCAPES[landKey];
  const flow = land.flows[flowKey] || Object.values(land.flows)[0];
  const step = flow.steps.find(s => s.id === selId) || flow.steps[0];
  const cols = Math.max(...flow.steps.map(s => s.col));
  // Narrow screens: the swimlane wraps into stacked bands of `perRow` columns each (no horizontal scroll).
  const CW = 216;
  const nb = Math.ceil(cols / Math.min(perRow, cols));
  const pr = Math.min(perRow, cols);
  const bandRows = Array.from({ length: nb }, (_, bi) => flow.lanes.map((l, ri) => ({ bi, l, ri }))
    .filter(({ l }) => nb === 1 || flow.steps.some(s => s.lane === l && s.col > bi * pr && s.col <= (bi + 1) * pr))).flat();
  const bandCols = bi => Array.from({ length: Math.min(pr, cols - bi * pr) }, (_, k) => bi * pr + k);

  const changeLang = l => { setLang(l); try { localStorage.setItem(LANG_KEY, l); } catch { /* ignore */ } };
  const pickLand = k => { setLandKey(k); setFlowKey(Object.keys(LANDSCAPES[k].flows)[0]); setSelId(null); setCoCode(null); };
  const pickFlow = k => { setFlowKey(k); setSelId(null); };

  // Connector arrows are measured from the rendered step cards.
  const drawEdges = useCallback(() => {
    const host = lanesRef.current; if (!host) return;
    const box = host.getBoundingClientRect();
    const out = [];
    flow.edges.forEach(([a, b]) => {
      const ea = host.querySelector(`[data-step="${a}"]`), eb = host.querySelector(`[data-step="${b}"]`);
      if (!ea || !eb) return;
      const A = ea.getBoundingClientRect(), B = eb.getBoundingClientRect();
      let d;
      if (Math.abs(A.top - B.top) < 4 && A.right < B.left) {
        const y = A.top + A.height / 2 - box.top; d = `M${A.right - box.left},${y} L${B.left - box.left - 2},${y}`;
      } else if (A.right < B.left - 4) {
        const x1 = A.right - box.left, y1 = A.top + A.height / 2 - box.top, x2 = B.left - box.left - 2, y2 = B.top + B.height / 2 - box.top, mx = (x1 + x2) / 2;
        d = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
      } else {
        const down = B.top > A.top, x1 = A.left + A.width / 2 - box.left, y1 = (down ? A.bottom : A.top) - box.top;
        const x2 = B.left + B.width / 2 - box.left, y2 = (down ? B.top - 2 : B.bottom + 2) - box.top, my = (y1 + y2) / 2;
        d = `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`;
      }
      out.push({ key: a + '-' + b, d });
    });
    setPaths(out);
  }, [flow]);

  useLayoutEffect(() => {
    const el = outerRef.current; if (!el) return undefined;
    const check = () => setPerRow(Math.max(1, Math.min(cols, Math.floor((el.clientWidth - 38) / CW))));
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    if (ro) ro.observe(el);
    return () => { if (ro) ro.disconnect(); };
  }, [cols, section]);
  useLayoutEffect(() => { drawEdges(); }, [drawEdges, lang, section, perRow]);
  useEffect(() => {
    window.addEventListener('resize', drawEdges);
    const host = lanesRef.current;
    const ro = typeof ResizeObserver !== 'undefined' && host ? new ResizeObserver(drawEdges) : null;
    if (ro) ro.observe(host);
    return () => { window.removeEventListener('resize', drawEdges); if (ro) ro.disconnect(); };
  }, [drawEdges, section]);

  // Company filter: per-company figures, or null when no company is selected.
  // 'single' = landscape with one company code (JB), whose totals are already company figures.
  const coMode = !coCode ? null
    : (COMPANIES[landKey] || []).length <= 1 ? 'single'
    : CO_FIGS[landKey] ? 'filter' : null;
  const coFigs = coMode === 'filter' ? (CO_FIGS[landKey][coCode] || {}) : null;
  const figOf = (fk, sid) => (coFigs ? (coFigs[fk] || {})[sid] || (coFigs[fk] ? null : { n: 0, docs: [] }) : null);
  const coInfo = (COMPANIES[landKey] || []).find(x => x.code === coCode);
  const stepFig = figOf(flowKey in land.flows ? flowKey : Object.keys(land.flows)[0], step.id);
  const stepDocs = stepFig && stepFig.docs ? stepFig.docs : step.docs;

  const modColor = l => MODULES[l]?.color || '#64748b';
  const c = modColor(step.lane);
  const numDocs = (stepDocs || []).filter(d => d[1] != null);
  const maxDoc = numDocs.length ? Math.max(...numDocs.map(d => d[1])) : 0;

  const companyObj = coCode ? (COMPANIES[landKey] || []).find(c => c.code === coCode) : null;
  const ST = lang === 'id'
    ? { overview: 'Gambaran umum', flow: 'Alur proses', company: 'Profil company', org: 'Organisasi & master data', about: 'Tentang landscape', notes: 'Temuan & catatan', tickets: 'Tiket Solman', doc: 'Cek dokumen', co: 'Company', all: 'Semua',
        pick: 'Pilih company di bar atas untuk melihat profil FICO & CO-PA-nya.', filtered: 'Angka diagram difilter ke', dim: 'kartu redup = tidak dipakai company ini',
        single: 'satu-satunya company di landscape ini, angka diagram sudah angka company ini', profile: 'Profil', clear: 'Hapus filter', units: 'Unit bisnis' }
    : { overview: 'Overview', flow: 'Process flow', company: 'Company profile', org: 'Organisation & master data', about: 'About landscape', notes: 'Findings & notes', tickets: 'Solman tickets', doc: 'Document check', co: 'Company', all: 'All',
        pick: 'Pick a company in the bar above to see its FICO & CO-PA profile.', filtered: 'Diagram figures filtered to', dim: 'dimmed cards = not used by this company',
        single: 'only company in this landscape, so the figures already belong to it', profile: 'Profile', clear: 'Clear filter', units: 'Business units' };
  const pill = on => `px-2.5 py-1 text-[12.5px] font-bold rounded-md ${on ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`;

  return (
    <div className="flex flex-col gap-3">
      {/* Sticky filter bar: landscape, company, language, sections */}
      <div className="sticky top-0 z-20">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-3.5 pt-2.5 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex items-baseline gap-2 min-w-0 mr-auto">
              <span className="text-[14px] font-bold text-slate-800">{U.title}</span>
              <span className="text-[11px] text-slate-400 font-mono hidden md:inline">{U.snapshot} {M.SNAP[landKey].takenOn} · {U.period} {lang === 'id' ? (M.SNAP[landKey].periodId || M.SNAP[landKey].period) : M.SNAP[landKey].period}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label="Language">
              <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 pl-1.5">{lang === 'id' ? 'Bahasa' : 'Language'}</span>
              {['id', 'en'].map(l => (
                <button key={l} type="button" onClick={() => changeLang(l)} className={pill(lang === l)}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {/* Landscape picker: its own centred row, larger than the other controls */}
          <div className="flex justify-center">
            <div className="inline-flex flex-wrap justify-center gap-2" role="group" aria-label="Landscape">
              {Object.entries(LANDSCAPES).map(([k, l]) => {
                const on = k === landKey;
                return (
                  <button key={k} type="button" onClick={() => pickLand(k)} title={tr(l.summary)}
                    className={`min-w-[150px] rounded-xl border-2 px-5 py-2 text-center transition ${on ? 'border-brand-600 bg-brand-50 shadow-sm' : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-slate-50'}`}>
                    <div className={`text-[16px] font-bold leading-tight ${on ? 'text-brand-700' : 'text-slate-700'}`}>{l.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{l.sid}{l.companies ? ` · ${l.companies.length} ${lang === 'id' ? 'company' : (l.companies.length > 1 ? 'companies' : 'company')}` : ''}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">{ST.co}</span>
            <button type="button" onClick={() => setCoCode(null)}
              className={`rounded-md px-2 py-0.5 text-[12px] border font-semibold ${!coCode ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-200 text-slate-600 hover:border-brand-400'}`}>
              {ST.all}
            </button>
            {(COMPANIES[landKey] || []).map(co => (
              <button key={co.code} type="button" onClick={() => setCoCode(c => c === co.code ? null : co.code)} title={co.name}
                className={`rounded-md px-2 py-0.5 text-[12px] border transition ${coCode === co.code ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-200 hover:border-brand-400 hover:bg-brand-50'}`}>
                <b className={`font-mono ${coCode === co.code ? 'text-white' : 'text-brand-700'}`}>{co.code}</b>{' '}
                <span className={coCode === co.code ? 'text-white/90' : 'text-slate-500'}>{co.short}</span>
              </button>
            ))}
          </div>
          {coMode && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-brand-50 border border-brand-100 px-2.5 py-1.5 text-[12px] text-slate-700">
              <span className="min-w-0">
                {coMode === 'single'
                  ? <><b className="font-mono text-brand-700">{coCode}</b> · {ST.single}</>
                  : <>{ST.filtered} <b className="font-mono text-brand-700">{coCode}</b> {coInfo?.short} <span className="text-slate-500">· {ST.dim}</span></>}
              </span>
              <span className="ml-auto flex gap-1.5">
                {section !== 'company' && companyObj && (
                  <button type="button" onClick={() => setSection('company')}
                    className="rounded-md border border-brand-300 bg-white px-2 py-0.5 text-[11.5px] font-semibold text-brand-700 hover:bg-brand-100">{ST.profile} {coCode} →</button>
                )}
                <button type="button" onClick={() => setCoCode(null)}
                  className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11.5px] font-semibold text-slate-600 hover:bg-slate-50">✕ {ST.clear}</button>
              </span>
            </div>
          )}
          <div className="flex gap-1 overflow-x-auto border-t border-slate-100 -mx-3.5 px-2" role="tablist">
            {[['overview', ST.overview], ['flow', ST.flow], ['company', ST.company + (coCode ? ' · ' + coCode : '')], ['org', ST.org], ['notes', ST.notes], ['tickets', ST.tickets], ['doc', ST.doc], ['about', ST.about]].map(([k, lbl]) => (
              <button key={k} type="button" role="tab" aria-selected={section === k} onClick={() => setSection(k)}
                className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 whitespace-nowrap ${section === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {section === 'overview' && (
        <SAPProcessMapOverview landKey={landKey} land={land} lang={lang} tr={tr} onOpenFlow={k => { pickFlow(k); setSection('flow'); }} />
      )}

      {/* About the landscape */}
      {section === 'about' && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col gap-3">
          <div>
            <div className="text-[15px] font-bold text-slate-800">{land.name} <span className="font-mono text-[11.5px] font-normal text-slate-400">{land.sid}</span></div>
            <p className="text-[13px] text-slate-600 mt-0.5 mb-0">{tr(land.summary)}</p>
            <div className="text-[11.5px] text-slate-400 mt-1">{U.subtitle}. {U.hint}</div>
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{ST.units}</div>
              <div className="flex flex-wrap gap-1.5">
                {land.companies.map(([code, name]) => (
                  <span key={code} className="border border-slate-200 rounded-md px-2 py-0.5 text-[12px]">
                    <b className="font-mono text-slate-600">{code}</b> <span className="text-slate-500">{tr(name)}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.character}</div>
              <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-0.5">{land.facts.character.map(x => <li key={x}>{tr(x)}</li>)}</ul>
            </div>
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.custom}</div>
              <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-0.5">{land.facts.custom.map(x => <li key={x}>{tr(x)}</li>)}</ul>
            </div>
          </div>
        </div>
      )}

      {/* Live document status inspector (insp. lot / PO / SO / order / maintenance plan) */}
      {section === 'doc' && <SAPDocInspector landKey={landKey} lang={lang} />}

      {/* Solman tickets classified per kind / module / master-data object / step */}
      {section === 'tickets' && (
        <SAPProcessMapTickets landKey={landKey} land={land} lang={lang} tr={tr} initialFilter={ticketFilter}
          onOpenStep={(fk, sid) => { pickFlow(fk); setSelId(sid); setSection('flow'); }} />
      )}

      {/* Findings recap per landscape (table procmap_note) */}
      {section === 'notes' && (
        <SAPProcessMapFindings landKey={landKey} lang={lang} find={M.FIND[landKey]} snap={M.SNAP[landKey]} live={M.live} />
      )}

      {/* Organisation, CO structure and master data */}
      {section === 'org' && (
        <>
          <COStructureCard key={landKey} landKey={landKey} lang={lang} defaultOpen />
          <SAPMasterDataCard key={'md-' + landKey} landKey={landKey} lang={lang} coCode={coCode} defaultOpen />
        </>
      )}

      {/* Company profile */}
      {section === 'company' && (companyObj
        ? <SAPCompanyPanel company={companyObj} companies={COMPANIES[landKey]} lang={lang} onClose={() => { setCoCode(null); setSection('flow'); }} />
        : <div className="bg-white border border-dashed border-slate-300 rounded-xl px-4 py-8 text-center text-[13px] text-slate-500">{ST.pick}</div>)}

      {section === 'flow' && (<>
      {/* Cycle tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2">
        {Object.entries(land.flows).map(([k, f]) => (
          <button key={k} type="button" onClick={() => pickFlow(k)}
            className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold border flex items-center gap-2 ${k === flowKey ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-700 border-slate-200 hover:border-brand-300'}`}>
            {tr(f.name)}
            <span className={`font-mono font-normal text-[11px] ${k === flowKey ? 'text-white/80' : 'text-slate-400'}`}>
              {coFigs ? (coFigs[k]?._vol?.[lang] || (coFigs[k] ? tr(f.vol) : (lang === 'id' ? 'tidak ada' : 'none'))) : tr(f.vol)}
            </span>
            {(() => {
              const g = (STD[landKey]?.[k]?.diffs || []).filter(d => d.status === 'gap').length;
              return g ? (
                <span className="text-[10.5px] font-bold px-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"
                  title={lang === 'id' ? 'Celah kontrol dibanding standar' : 'Control gaps versus standard'}>
                  {g} {lang === 'id' ? 'celah' : 'gap'}{lang !== 'id' && g > 1 ? 's' : ''}
                </span>
              ) : null;
            })()}
          </button>
        ))}
      </div>

      <div>
        <div className="text-[17px] font-bold text-slate-800">{land.name} · {tr(flow.name)}</div>
        <p className="text-[13px] text-slate-500 mt-0.5 max-w-4xl">{tr(flow.desc)}</p>
        <div className="flex flex-wrap gap-3 mt-2 text-[12px] text-slate-500">
          {flow.lanes.map(l => (
            <span key={l} className="flex items-center gap-1.5">
              <i className="inline-block w-3 h-3 rounded-sm" style={{ background: modColor(l) }} />{l} · {MODULES[l].label}
            </span>
          ))}
        </div>
      </div>

      {/* Swimlane */}
      <div ref={outerRef} className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
        <div ref={lanesRef} className="relative grid" style={{ gridTemplateColumns: `36px repeat(${pr}, ${CW}px)`, minWidth: 'max-content' }}>
          {bandRows.map(({ bi, l, ri }) => (
            <div key={bi + l} className="contents">
              <div className={`sticky left-0 z-[2] bg-white border-r border-slate-200 flex items-center justify-center font-mono text-[13px] font-semibold ${bi && !ri ? 'border-t-2 border-t-slate-300' : ri ? 'border-t border-slate-100' : ''}`}
                style={{ gridRow: bi * flow.lanes.length + ri + 1, gridColumn: 1, color: modColor(l), writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{l}</div>
              {bandCols(bi).map((ci, lci) => (
                <div key={ci} className={`flex flex-col gap-4 items-center justify-center px-2 py-3 min-h-[120px] ${bi && !ri ? 'border-t-2 border-t-slate-300' : ri ? 'border-t border-slate-100' : ''}`}
                  style={{ gridRow: bi * flow.lanes.length + ri + 1, gridColumn: lci + 2 }}>
                  {flow.steps.filter(s => s.lane === l && s.col === ci + 1).map(s => {
                    const sc = modColor(s.lane), sel = s.id === step.id;
                    const fig = s.gen ? null : figOf(flowKey, s.id);
                    const unused = fig && (fig.n === 0 || (fig.n == null && fig.docs && !fig.docs.length));
                    return (
                      <button key={s.id} type="button" data-step={s.id} onClick={() => setSelId(s.id)}
                        className={`relative z-[1] w-full text-left bg-white border border-slate-200 rounded-md px-3 py-2.5 flex flex-col gap-1 hover:border-slate-400 ${unused ? 'opacity-45' : ''}`}
                        style={{ borderLeft: `4px solid ${sc}`, boxShadow: sel ? `0 0 0 2px ${sc}` : undefined }}>
                        <span className="font-mono text-[11px] text-slate-400">{s.lane} · {U.step} {flow.steps.indexOf(s) + 1}</span>
                        <span className="font-semibold text-[13.5px] leading-tight text-slate-800">{tr(s.t)}</span>
                        <span className="font-mono text-[12px]" style={{ color: sc }}>{tr(s.tc)}</span>
                        {fig?.v
                          ? <span className="font-mono text-[11.5px] text-brand-700 font-semibold">{fig.v[lang] || fig.v.en} <span className="font-normal text-slate-400">· {coCode}</span></span>
                          : unused
                            ? <span className="font-mono text-[11.5px] text-slate-400">{lang === 'id' ? `tidak dipakai ${coCode}` : `not used by ${coCode}`}</span>
                            : s.v && <span className="font-mono text-[11.5px] text-slate-500">{tr(s.v)}</span>}
                        {s.custom && <span className="font-mono text-[10.5px] px-1.5 rounded border border-rose-400 text-rose-600 w-max">{U.customCount(s.custom.length)}</span>}
                        {s.gen && <span className="font-mono text-[10.5px] px-1.5 rounded border border-slate-300 text-slate-500 w-max">{U.general}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
          <svg className="absolute inset-0 pointer-events-none z-0 overflow-visible" width="100%" height="100%">
            <defs>
              <marker id="pmap-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill="#94a3b8" />
              </marker>
            </defs>
            {paths.map(p => <path key={p.key} d={p.d} fill="none" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#pmap-arrow)" />)}
          </svg>
        </div>
      </div>


      {/* Step detail */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 grid gap-4" style={{ borderTop: `4px solid ${c}`, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div className="col-span-full text-[16px] font-bold text-slate-800">
          {tr(step.t)} <span className="font-mono text-[13px] font-normal ml-1" style={{ color: c }}>{tr(step.tc)}</span>
        </div>
        {stepFig && stepFig.docs && !stepFig.docs.length && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.docsUsed} · {coCode}</div>
            <p className="text-[12.5px] text-slate-500 m-0">{lang === 'id' ? `Tidak ada dokumen untuk company ${coCode} dalam periode snapshot.` : `No documents for company ${coCode} in the snapshot period.`}</p>
          </div>
        )}
        {stepDocs && !!stepDocs.length && (maxDoc ? (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.docsUsed}{stepFig?.docs ? ` · ${coCode}` : ''}</div>
            {stepDocs.map(([label, n]) => (
              <div key={label} className="grid items-center gap-2 text-[12.5px]" style={{ gridTemplateColumns: 'minmax(0,1fr) 64px' }}>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="truncate text-slate-700" title={tr(label)}>{tr(label)}</span>
                  <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full" style={{ width: `${((n || 0) / maxDoc) * 100}%`, background: c }} /></div>
                </div>
                <span className="font-mono text-right tabular-nums text-slate-700">{n != null ? numFmt(n, lang) : '–'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.docsConfigured}</div>
            <ul className="list-disc pl-4 text-[12.5px] text-slate-600">{step.docs.map(([label]) => <li key={label}>{tr(label)}</li>)}</ul>
          </div>
        ))}
        {step.custom && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.customPoints}</div>
            <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-0.5">{step.custom.map(x => <li key={x}>{tr(x)}</li>)}</ul>
          </div>
        )}
        {step.fi && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.fiImpact}</div>
            <p className="text-[12.5px] text-slate-600 m-0">{tr(step.fi)}</p>
          </div>
        )}
        {step.notes && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.notes}</div>
            <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-0.5">{step.notes.map(x => <li key={x}>{tr(x)}</li>)}</ul>
          </div>
        )}
        {step.gen && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{U.dataStatus}</div>
            <p className="text-[12.5px] text-slate-600 m-0">{U.genText}</p>
          </div>
        )}
      </div>

      <SAPProcessMapIssues landKey={landKey} flowKey={flowKey in land.flows ? flowKey : Object.keys(land.flows)[0]} step={step} coCode={coCode} color={c} lang={lang}
        onOpenTickets={f => { setTicketFilter(f); setSection('tickets'); }} />

      <StandardCompare std={STD[landKey]?.[flowKey in land.flows ? flowKey : Object.keys(land.flows)[0]]} lang={lang}
        open={stdOpen} onToggle={() => setStdOpen(o => !o)} />
      </>)}

      <div className="text-[11.5px] text-slate-400 leading-relaxed max-w-5xl">{U.source}</div>
    </div>
  );
}
